import { ChevronDown, ChevronRight, Maximize2, TriangleAlert } from 'lucide-react';
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDeviceConnections, type ConnectionNetwork, type DeviceConnection } from '../../data/hooks';
import { i18n } from '../../i18n';
import { useDeviceLive } from '../../realtime/hooks';
import { Card, Modal, SignalBars } from '../../ui/components';
import { formatBytes, formatDateTime, formatMetric, formatNumber } from '../../ui/format';
import { signalBars, type SignalSource } from '../../ui/signal';
import { cellLabel, operatorName, ratLabel } from './plmn';
import './ConnectionsCard.css';
import LOGIN_SIGNAL from './loginSignal.json';
import { usePrefs } from '../../app/prefs';

const LIMITS = [20, 50, 200] as const;
/** Rows shown in the compact card on the device page; the overlay shows the full list. */
const COMPACT_ROWS = 5;
/** A closed connection shorter than this is flagged (icon + legend); no cause is inferred from it. */
const SHORT_MIN = 15;
const SHORT_S = SHORT_MIN * 60;
const REASONS = new Set([
  'superseded',
  'idle_timeout',
  'gateway_restart',
  'gateway_shutdown',
  'not_in_snapshot',
  'peer_closed',
  'detect_timeout',
  'protocol_error',
  'resource_limit',
  'unknown',
]);

/** "7 min", "5 h 46 min", "2 d 3 h"; sub-minute durations in seconds. */
export function formatDuration(s: number): string {
  const sec = Math.max(0, Math.round(s));
  if (sec < 60) return i18n.t('devices.connections.duration.seconds', { s: sec });
  const m = Math.floor(sec / 60);
  if (m < 60) return i18n.t('devices.connections.duration.minutes', { m });
  const h = Math.floor(m / 60);
  if (h < 24) return i18n.t('devices.connections.duration.hoursMinutes', { h, m: m % 60 });
  return i18n.t('devices.connections.duration.daysHours', { d: Math.floor(h / 24), h: h % 24 });
}

const reasonKey = (r: string | null | undefined) =>
  `devices.connections.reason.${r && REASONS.has(r) ? r : 'unknown'}` as 'devices.connections.reason.unknown';

/** Fetches the list; a presence change of THIS device (and only this device) refetches it so an open row closes/appears. */
function useConnections(id: string, limit: number) {
  const q = useDeviceConnections(id, limit);
  const presence = useDeviceLive(id, (d) => d?.presence);
  const seen = useRef(presence);
  const refetch = q.refetch;
  useEffect(() => {
    if (seen.current === presence) return;
    seen.current = presence;
    void refetch();
  }, [presence, refetch]);
  return q;
}

/** The "!" rows explained: a closed connection shorter than SHORT_MIN minutes. */
function Legend() {
  const { t } = useTranslation();
  return (
    <p className="conn-legend small muted">
      <span className="conn-short-icon" aria-hidden>
        <TriangleAlert size={12} />
      </span>
      {t('devices.connections.legend', { minutes: SHORT_MIN })}
    </p>
  );
}

/** Loading / error / empty states shared by the card and the overlay. */
function ListState({
  q,
  children,
}: {
  q: ReturnType<typeof useConnections>;
  children: (items: DeviceConnection[]) => ReactNode;
}) {
  const { t } = useTranslation();
  const items = q.data?.items ?? [];
  const msg = q.isLoading
    ? t('common.loading')
    : q.isError
      ? t('devices.connections.unavailable')
      : items.length === 0
        ? t('devices.connections.empty')
        : undefined;
  if (msg !== undefined)
    return (
      <p className="muted small" style={{ margin: 0 }}>
        {msg}
      </p>
    );
  return <>{children(items)}</>;
}

/**
 * Connection / cellular history of ONE device (docs/architecture/14) on the device page: compact card with the latest
 * COMPACT_ROWS connections (start, operator, lifetime, close reason) and a button that opens the full history in a
 * large overlay (ConnectionsOverlay). The card and the overlay's default page share one query (limit 20).
 */
export function ConnectionsCard({ id }: { id: string }) {
  const { t } = useTranslation();
  const [full, setFull] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const q = useConnections(id, LIMITS[0]);
  const close = useCallback(() => setFull(false), []);
  // formatDateTime reads the preference outside React: subscribe so start/end times re-render when the user changes it
  usePrefs((p) => p.dateFormat);
  return (
    <>
      <Card
        title={t('devices.connections.title')}
        actions={
          <button
            ref={opener}
            type="button"
            className="btn btn-ghost conn-open-full"
            onClick={() => setFull(true)}
          >
            <Maximize2 size={14} aria-hidden />
            {t('devices.connections.openFull')}
          </button>
        }
      >
        <Legend />
        <ListState q={q}>
          {(items) => (
            <div className="table-scroll">
              {!reportsNetwork(items) && <NoNetworkNote />}
              <table className="table conn-table conn-compact">
                <caption className="conn-caption">
                  {t('devices.connections.latest', { n: Math.min(items.length, COMPACT_ROWS) })}
                </caption>
                <thead>
                  <tr>
                    <th>{t('devices.connections.start')}</th>
                    {reportsNetwork(items) && <th>{t('devices.connections.operator')}</th>}
                    <th>{t('devices.connections.lifetime')}</th>
                    <th>{t('devices.connections.reason.title')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.slice(0, COMPACT_ROWS).map((c) => (
                    <CompactRow key={c.id} c={c} showNetwork={reportsNetwork(items)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ListState>
      </Card>
      {full && <ConnectionsOverlay id={id} onClose={close} returnFocus={opener} />}
    </>
  );
}

/** The full history: every column, the 20/50/200 selector and the expandable detail rows, in a large modal. */
function ConnectionsOverlay({
  id,
  onClose,
  returnFocus,
}: {
  id: string;
  onClose: () => void;
  returnFocus: RefObject<HTMLButtonElement | null>;
}) {
  const { t } = useTranslation();
  const [limit, setLimit] = useState<number>(LIMITS[0]);
  const q = useConnections(id, limit);
  return (
    <Modal
      title={t('devices.connections.fullTitle')}
      onClose={onClose}
      returnFocus={returnFocus}
      actions={
        <div className="segmented conn-limit" role="group" aria-label={t('devices.connections.show')}>
          {LIMITS.map((n) => (
            <button key={n} type="button" aria-pressed={limit === n} onClick={() => setLimit(n)}>
              {n}
            </button>
          ))}
        </div>
      }
    >
      <Legend />
      <ListState q={q}>
        {(items) => (
          <div className="table-scroll">
            {!reportsNetwork(items) && <NoNetworkNote />}
            <table className="table conn-table">
              <thead>
                <tr>
                  <th aria-label={t('devices.connections.details')} />
                  <th>{t('devices.connections.start')}</th>
                  {reportsNetwork(items) && <th>{t('devices.connections.operator')}</th>}
                  {reportsNetwork(items) && <th>{t('devices.connections.cell')}</th>}
                  <th>{t('devices.connections.signal')}</th>
                  <th>{t('devices.connections.lifetime')}</th>
                  <th>{t('devices.connections.reason.title')}</th>
                  <th>{t('devices.connections.address')}</th>
                  <th>{t('devices.connections.firmware')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <ConnectionRow key={c.id} c={c} showNetwork={reportsNetwork(items)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ListState>
    </Modal>
  );
}

const isShort = (c: DeviceConnection) =>
  c.disconnectedAt !== null && c.durationS !== null && c.durationS < SHORT_S;

/** Start time (always with seconds) and, for a short closed connection, the "!" icon with its tooltip. */
function StartCell({ c }: { c: DeviceConnection }) {
  const { t } = useTranslation();
  const short = isShort(c);
  const tip = short
    ? t('devices.connections.shortTooltip', {
        minutes: SHORT_MIN,
        duration: formatDuration(c.durationS ?? 0),
      })
    : undefined;
  return (
    <span className="conn-start">
      {tip && (
        <span className="conn-short-icon" role="img" aria-label={tip} title={tip}>
          <TriangleAlert size={14} aria-hidden />
        </span>
      )}
      <time dateTime={c.connectedAt}>{formatDateTime(c.connectedAt)}</time>
    </span>
  );
}

/**
 * Operator from the PLMN the modem reported at login (plmn.ts), with the RAT badge. Nothing else is ever used as a
 * source: a protocol's raw operator code can be the SIM's home network (IMSI prefix), not the visited one
 * (docs/protocols).
 */
function OperatorCell({ net }: { net: ConnectionNetwork | undefined }) {
  const { t } = useTranslation();
  const plmn = net?.plmn ?? (net?.mcc && net?.mnc ? `${net.mcc}-${net.mnc}` : undefined);
  const operator = operatorName(plmn);
  if (!operator)
    return (
      <abbr className="muted conn-no-operator" title={t('devices.connections.operatorUnknown')}>
        —
      </abbr>
    );
  const rat = ratLabel(net?.rat);
  return (
    <span className="conn-operator">
      <span>{operator}</span>
      {rat && <span className="badge badge-info conn-rat">{rat}</span>}
    </span>
  );
}

function LifetimeCell({ c }: { c: DeviceConnection }) {
  const { t } = useTranslation();
  if (c.disconnectedAt === null)
    return (
      <span className="status status-online">
        <span className="dot conn-live" aria-hidden />
        {t('devices.connections.open')}
      </span>
    );
  return c.durationS !== null ? <>{formatDuration(c.durationS)}</> : <span className="muted">—</span>;
}

function ReasonCell({ c }: { c: DeviceConnection }) {
  const { t } = useTranslation();
  return c.disconnectedAt === null ? (
    <span className="muted">—</span>
  ) : (
    <>{t(reasonKey(c.disconnectReason))}</>
  );
}

/** True when at least one connection's login reported the visited mobile network (docs/protocols). */
const reportsNetwork = (items: DeviceConnection[]) =>
  items.some((c) => Boolean(c.login?.network?.plmn || (c.login?.network?.mcc && c.login?.network?.mnc)));

/** Shown instead of empty Operator / Cell columns when the device never reports its mobile network. */
function NoNetworkNote() {
  const { t } = useTranslation();
  return <p className="conn-legend small muted">{t('devices.connections.noNetwork')}</p>;
}

const CompactRow = memo(function CompactRow({
  c,
  showNetwork,
}: {
  c: DeviceConnection;
  showNetwork: boolean;
}) {
  const { t } = useTranslation();
  const isOpen = c.disconnectedAt === null;
  return (
    <tr className={`conn-row${isShort(c) ? ' is-short' : ''}${isOpen ? ' is-open' : ''}`}>
      <td data-label={t('devices.connections.start')}>
        <StartCell c={c} />
      </td>
      {showNetwork && (
        <td data-label={t('devices.connections.operator')}>
          <OperatorCell net={c.login?.network} />
        </td>
      )}
      <td data-label={t('devices.connections.lifetime')}>
        <LifetimeCell c={c} />
      </td>
      <td data-label={t('devices.connections.reason.title')}>
        <ReasonCell c={c} />
      </td>
    </tr>
  );
});

const ConnectionRow = memo(function ConnectionRow({
  c,
  showNetwork,
}: {
  c: DeviceConnection;
  showNetwork: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const net = c.login?.network;
  const isOpen = c.disconnectedAt === null;
  const cell = cellLabel(net?.cell_id, net?.band);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <Fragment>
      <tr className={`conn-row${isShort(c) ? ' is-short' : ''}${isOpen ? ' is-open' : ''}`}>
        <td className="conn-toggle">
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            aria-expanded={open}
            aria-label={t('devices.connections.details')}
            onClick={() => setOpen((v) => !v)}
          >
            <Chevron size={16} aria-hidden />
          </button>
        </td>
        <td data-label={t('devices.connections.start')}>
          <StartCell c={c} />
        </td>
        {showNetwork && (
          <td data-label={t('devices.connections.operator')}>
            <OperatorCell net={net} />
          </td>
        )}
        {showNetwork && (
          <td data-label={t('devices.connections.cell')} className="mono">
            {cell ?? <span className="muted">—</span>}
          </td>
        )}
        <td data-label={t('devices.connections.signal')}>
          <Signal net={net} protocol={c.protocol} />
        </td>
        <td data-label={t('devices.connections.lifetime')}>
          <LifetimeCell c={c} />
        </td>
        <td data-label={t('devices.connections.reason.title')}>
          <ReasonCell c={c} />
        </td>
        <td data-label={t('devices.connections.address')} className="mono">
          {c.remoteAddr ?? <span className="muted">—</span>}
        </td>
        <td data-label={t('devices.connections.firmware')} className="mono">
          {c.firmware ?? <span className="muted">—</span>}
        </td>
      </tr>
      {open && (
        <tr className="conn-detail">
          <td colSpan={showNetwork ? 9 : 7}>
            <Details c={c} />
          </td>
        </tr>
      )}
    </Fragment>
  );
});

/**
 * The login's own signal figure has a different scale per protocol (catalog data: loginSignal.json, docs/protocols);
 * a protocol without a known scale is shown raw, without bars.
 */
type LoginSignalKey =
  'devices.connections.signalCsq' | 'devices.connections.signalPct' | 'devices.connections.signalRaw';

function loginSignal(protocol: string | null): { source?: SignalSource; key: LoginSignalKey } {
  const scale = protocol
    ? (LOGIN_SIGNAL as unknown as Record<string, { source: string; scale: string } | undefined>)[protocol]
    : undefined;
  if (scale?.scale === 'csq')
    return { source: scale.source as SignalSource, key: 'devices.connections.signalCsq' };
  if (scale?.scale === 'pct')
    return { source: scale.source as SignalSource, key: 'devices.connections.signalPct' };
  return { key: 'devices.connections.signalRaw' };
}

/** RSRP/SINR when the modem reported LTE measurements, else the protocol's own signal figure. */
function Signal({ net, protocol }: { net: ConnectionNetwork | undefined; protocol: string | null }) {
  const { t } = useTranslation();
  if (!net) return <span className="muted">—</span>;
  let source: SignalSource | undefined;
  let value: number | undefined;
  if (typeof net.rsrp_dbm === 'number') {
    source = 'network.rsrp_dbm';
    value = net.rsrp_dbm;
  } else if (typeof net.signal === 'number') {
    source = loginSignal(protocol).source;
    value = net.signal;
  }
  const bars = source !== undefined && value !== undefined ? signalBars(source, value) : undefined;
  const parts: string[] = [];
  if (typeof net.rsrp_dbm === 'number') parts.push(`RSRP ${formatMetric('network.rsrp_dbm', net.rsrp_dbm)}`);
  if (typeof net.sinr_db === 'number') parts.push(`SINR ${formatMetric('network.sinr_db', net.sinr_db)}`);
  if (parts.length === 0 && typeof net.signal === 'number')
    parts.push(t(loginSignal(protocol).key, { value: formatNumber(net.signal) }));
  if (parts.length === 0 && typeof net.rxlev_dbm === 'number')
    parts.push(`RxLev ${formatNumber(net.rxlev_dbm)} dBm`);
  if (parts.length === 0) return <span className="muted">—</span>;
  const text = parts.join(' · ');
  return (
    <span className="conn-signal">
      {bars !== undefined && source && value !== undefined && (
        <SignalBars
          bars={bars}
          title={
            bars
              ? t('devices.signal.level', { bars, value: formatMetric(source, value) })
              : t('devices.signal.none', { value: formatMetric(source, value) })
          }
        />
      )}
      <span className="small">{text}</span>
    </span>
  );
}

/** Every stored field of the connection, for the expanded row. */
function Details({ c }: { c: DeviceConnection }) {
  const { t } = useTranslation();
  const net = c.login?.network ?? {};
  const raw = c.login?.raw ?? {};
  const netEntries = Object.entries(net).filter(([, v]) => v !== undefined && v !== null && v !== '');
  const rawEntries = Object.entries(raw);
  return (
    <div className="conn-details">
      <dl className="kv">
        <dt>{t('devices.connections.fields.connectedAt')}</dt>
        <dd>{formatDateTime(c.connectedAt)}</dd>
        <dt>{t('devices.connections.fields.identifiedAt')}</dt>
        <dd>{c.identifiedAt ? formatDateTime(c.identifiedAt) : '—'}</dd>
        <dt>{t('devices.connections.fields.disconnectedAt')}</dt>
        <dd>{c.disconnectedAt ? formatDateTime(c.disconnectedAt) : t('devices.connections.open')}</dd>
        <dt>{t('devices.connections.lifetime')}</dt>
        <dd>
          {c.durationS !== null ? `${formatDuration(c.durationS)} (${formatNumber(c.durationS)} s)` : '—'}
        </dd>
        <dt>{t('devices.connections.reason.title')}</dt>
        <dd>{c.disconnectReason ? `${t(reasonKey(c.disconnectReason))} (${c.disconnectReason})` : '—'}</dd>
        <dt>{t('devices.connections.fields.previousDisconnectReason')}</dt>
        <dd>
          {c.previousDisconnectReason
            ? `${t(reasonKey(c.previousDisconnectReason))} (${c.previousDisconnectReason})`
            : '—'}
        </dd>
        <dt>{t('devices.connections.fields.gapSincePrevious')}</dt>
        <dd>{c.gapSincePreviousS !== null ? formatDuration(c.gapSincePreviousS) : '—'}</dd>
        <dt>{t('devices.connections.fields.protocol')}</dt>
        <dd className="mono">{c.protocol ?? '—'}</dd>
        <dt>{t('devices.connections.address')}</dt>
        <dd className="mono">
          {c.remoteAddr ?? '—'}
          {c.localPort !== null ? ` → :${c.localPort}` : ''}
        </dd>
        <dt>{t('devices.connections.firmware')}</dt>
        <dd className="mono">{c.firmware ?? '—'}</dd>
        <dt>{t('devices.connections.fields.accessTime')}</dt>
        <dd>{c.login?.access_time_s !== undefined ? `${formatNumber(c.login.access_time_s)} s` : '—'}</dd>
        <dt>{t('devices.connections.fields.counters')}</dt>
        <dd>
          {c.counters
            ? t('devices.connections.fields.countersValue', {
                rx: formatBytes(c.counters.rx_bytes),
                tx: formatBytes(c.counters.tx_bytes),
                rxSeg: formatNumber(c.counters.rx_segments),
                txSeg: formatNumber(c.counters.tx_segments),
              })
            : '—'}
        </dd>
      </dl>
      {netEntries.length > 0 && (
        <>
          <h4 className="conn-details-h">{t('devices.connections.fields.network')}</h4>
          <dl className="kv">
            {netEntries.flatMap(([k, v]) => [
              <dt key={`${k}-k`} className="mono">
                {k}
              </dt>,
              <dd key={`${k}-v`} className="mono conn-wrap">
                {String(v)}
              </dd>,
            ])}
          </dl>
        </>
      )}
      {rawEntries.length > 0 && (
        <>
          <h4 className="conn-details-h">{t('devices.connections.fields.raw')}</h4>
          <dl className="kv">
            {rawEntries.flatMap(([k, v]) => [
              <dt key={`${k}-k`} className="mono">
                {k}
              </dt>,
              <dd key={`${k}-v`} className="mono conn-wrap">
                {v}
              </dd>,
            ])}
          </dl>
        </>
      )}
    </div>
  );
}
